import { useEffect } from 'react';
import type { CmsExternalServiceLink } from '@gov-nx/api';
import {
	type RezaChangeData,
	type RezaChangeFields,
	useFormSession,
	useRezaChangeFormDefinition,
	useRezaEligibleList,
} from '@gov-nx/core/hooks';
import type { FormDefinition2 } from '@gov-nx/core/service';
import { useFormBuilder, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { hasProperties, is } from '@gov-nx/core/types';
import { PageCode, zastupujiciOsobaNazev } from '@gov-nx/module/page';
import { useBoolean } from '@gov-nx/utils/common';

export type Data = RezaChangeData;

export type Fields = RezaChangeFields;

interface RepresentationFormProps {
	link: CmsExternalServiceLink['link'];
	nazev?: string;
	isRepresentationAllowed?: boolean;
}

export interface RepresentationFormType {
	form: FormDefinition2<Data, Fields>;
	role: Data['role'];
	isRepresentationAllowed: boolean;
	hasRepresentationOptions: boolean;
	isEligibleListLoading: boolean;
	isFormSessionLoading: boolean;
	isSubmitButtonDisabled: boolean;
	redirectUrl: Nullable<string>;
	onSubmit: () => void;
}

export const useRepresentationForm = ({
	link,
	nazev,
	isRepresentationAllowed = true,
}: RepresentationFormProps): RepresentationFormType => {
	const { t } = useLocale(PageCode['rozcestnik']);
	// zatim kody sablon doplneny do cms linku sluzby ve string formatu: &kodySablon=kod,kod,kod
	const params = Object.fromEntries(new URLSearchParams(link));
	const kodSablony = params?.kodySablon?.split(',').filter(is);
	const isRepresentationRoleChecked = useBoolean();

	const formSession = useFormSession();
	const eligibleList = useRezaEligibleList({
		params: {
			role: 'zmocnenec',
		},
		filter: {
			kodSablony,
		},
		enabled: isRepresentationAllowed && isRepresentationRoleChecked.isTrue,
		ignoreError: true,
	});

	const options =
		eligibleList.data?.seznam
			?.filter(hasProperties(['kod', 'nazevSablony']))
			.map((reza) => ({
				label: `${zastupujiciOsobaNazev(reza)}: ${reza.nazevSablony}`,
				value: reza.kod,
			})) ?? [];

	const hasRepresentationOptions = !!options.length;
	const isEligibleListLoading = eligibleList.isInitialLoading;
	const isFormSessionLoading = formSession.isLoading || formSession.isSuccess;

	const rezaChangeFields = useRezaChangeFormDefinition({
		isRepresentationAllowed,
		options,
		fields: {
			role: {
				label: t('smart-forms.role.label'),
				required: {
					value: true,
					message: t('smart-forms.role.povinny'),
				},
				withoutRepresentationLabel: t('smart-forms.role.bez-zastupovani'),
				withRepresentationLabel: t('smart-forms.role.v-zastoupeni'),
			},
			rezaCode: {
				label: t('smart-forms.reza-kod.label'),
				requiredMessage: t('smart-forms.reza-kod.povinny'),
				selectPlaceholder: t('smart-forms.reza-kod.vyberte'),
			},
		},
	});

	const form = useFormBuilder<Data, Fields>(rezaChangeFields);
	const role = form.formMethods.watch('role');

	useEffect(() => {
		if (role === 'v_zastoupeni') {
			isRepresentationRoleChecked.setTrue();
		} else {
			isRepresentationRoleChecked.setFalse();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [role]);

	const onSubmit = form.formMethods.handleSubmit((data) =>
		formSession.submit({
			paramsString: link,
			rezaCode:
				data.role === 'v_zastoupeni' ? (data.rezaCode ?? undefined) : undefined,
			nazev: nazev ?? undefined,
		})
	);

	return {
		form,
		role,
		isRepresentationAllowed,
		hasRepresentationOptions,
		isEligibleListLoading,
		isFormSessionLoading,
		isSubmitButtonDisabled:
			isEligibleListLoading ||
			isFormSessionLoading ||
			(role === 'v_zastoupeni' &&
				eligibleList.isFetched &&
				!hasRepresentationOptions),
		redirectUrl: formSession.redirectUrl,
		onSubmit,
	};
};
